import { isMobileLayout } from '@psl/components/styles/responsive';
import { publicConfigQuery$ } from '@psl/functions/firebase/queries/public-config/publicConfigQuery$';
import { getIsCurrentUserSeller } from '@psl/functions/user/userTypeGlobalState';
import {
  getPathFromState,
  getStateFromPath,
  LinkingOptions,
  NavigationState,
  PartialRoute,
  PartialState,
  Route,
} from '@react-navigation/native';
import qs from 'qs';
import * as R from 'ramda';
import { Linking, Platform } from 'react-native';
import { firstValueFrom } from 'rxjs';
import type { AppModalParams } from '../../../flows/Routes';
import { makeDebug } from '../../../utils/makeDebug';
import { currentUserId } from '../../auth/utils';
import { config } from '../../config';
import { isDebugEnabled } from '../../debugging';
import { tryCaptureCampaignId } from '../../dynamic-links/campaignTracker';
import { resolveStateFromPath } from '../resolveStateFromPath';
import {
  getInitialPath,
  resolveInitialUrl,
  setInitialPath,
  setInitialState,
  updateInitialUrl,
} from './initial-url';
import { screens } from './screens';

const debug = makeDebug('navigation:linking', isDebugEnabled('navigation'));

export function getDeepLinkUrlFromState(
  state: NavigationState,
  options?: any,
  clean?: boolean,
): string {
  return (linking.getPathFromState as any)(state, options, clean);
}

export const linking: LinkingOptions<AppModalParams> = {
  prefixes: [config.url, `${config.scheme}://`],

  async getInitialURL() {
    const url = await resolveInitialUrl();
    debug('resolved initial url: %s', url);
    return url;
  },

  // we do it like this to preserve show on reload
  getPathFromState: (state, options, clean?: boolean) => {
    const newState = clean !== false ? cleanRouteState(state) : state;
    const path =
      newState == null
        ? '/'
        : serializeQs(newState, getPathFromState(newState, options));

    const pathComponents = path.split('/');
    const invalidIndex = pathComponents.findIndex(c => c === 'undefined');

    const res =
      invalidIndex >= 0
        ? pathComponents.slice(0, invalidIndex).join('/')
        : path;

    if (res.startsWith('/user/') || res.startsWith('/user')) {
      // find the target user route.
      let lastRouteName = '';
      const findRoute = (state: State | undefined): any => {
        if (!state?.routes == null || state?.index == null) return null;
        const route = state.routes[state.index];
        if (route.name === 'user' && lastRouteName === 'home') {
          return route;
        }
        lastRouteName = route.name;
        return findRoute(route.state);
      };

      const targetRoute = findRoute(state);

      if (
        targetRoute?.params?.alias === 'storefront' ||
        getIsCurrentUserSeller()
      ) {
        const temp = res.replace(/^\/user/, '/storefront');
        const tempUrl = new URL(`http://localhost${temp}`);
        tempUrl.searchParams.delete('alias');
        return tempUrl
          .toString()
          .replace('http://localhost', '')
          .replace('/main', '');
      }

      return res
        .replace(/^\/user/, '/me')
        .replace(/^\/me\/activities/, '/me')
        .replace('/main', '');
    }

    return res;

    type State = NavigationState | Omit<PartialState<NavigationState>, 'stale'>;
    /**
     * The original `getPathFromState` can only serialize `params` like:
     *
     *    { a: { b: 1 }, c() { ... }} => `a=[object Object]&c=function()...`
     *
     * this function can do it in a right way:
     *
     *    { a: { b: 1 }, c() { ... }} => `a[b]=1`
     *
     * and replace the right querystring into the generated path
     */
    function serializeQs(state: State, originalPath: string): string {
      originalPath = originalPath.replace(/^\/%40/, '/@');

      const splittedPath = originalPath.split('?');

      const isExistsQueryString = splittedPath.length >= 2;
      if (!isExistsQueryString) return originalPath;

      const initParts = splittedPath.slice(0, -1);
      const lastPart = splittedPath[splittedPath.length - 1];
      const searchParams = new URLSearchParams(lastPart);
      const utmParams = new URLSearchParams(
        [...searchParams].filter(x => x[0].startsWith('utm_')),
      );

      const activeRoute = getActiveRoute(state);

      // todo(andyl): move this out of this function later.
      if (activeRoute.name === 'storeListingDetail') {
        const utm_query_string = utmParams.toString();
        return (
          initParts.join('') + (utm_query_string ? '?' + utm_query_string : '')
        );
      }

      const allParams: Record<string, any> = activeRoute.params ?? {};
      /**
       * `params` is existed in both url path and query string, like:
       *
       *    /:urlCode?:params1&:param2
       *
       *  We only need to serialize the params which be included in the query
       *  string
       */
      const paramsInQuery = Object.keys(qs.parse(lastPart)).reduce(
        (result, key) => ({
          ...result,
          [key]: allParams[key],
        }),
        {} as Record<string, any>,
      );

      const newLastPart = qs.stringify(paramsInQuery);

      return [...initParts, newLastPart].filter(Boolean).join('?');
    }
    function getActiveRoute(state: State): { name: string; params?: object } {
      const route =
        typeof state.index === 'number'
          ? state.routes[state.index]
          : state.routes[state.routes.length - 1];

      if (route.state) {
        return getActiveRoute(route.state);
      }

      return route;
    }

    function cleanRouteState(
      state: NavigationState | Omit<PartialState<NavigationState>, 'stale'>,
    ): null | typeof state {
      const lastViewableRouteIdx = R.findLastIndex(
        route => !route.name.startsWith('_'),
        state.routes.slice(0, (state.index ?? 0) + 1) as PartialRoute<
          Route<string, object | undefined>
        >[],
      );

      if (lastViewableRouteIdx < 0) return null;

      const lastViewableRoute = state.routes[lastViewableRouteIdx];
      let newState = lastViewableRoute.state;
      if (lastViewableRoute.state) {
        newState = cleanRouteState(lastViewableRoute.state) ?? undefined;
      }

      const cleanParams = (params: any): any => {
        if (params == null) return params;

        const { screen, params: _params, ...rest } = params;

        return {
          screen,
          params: cleanParams(_params),
          ...Object.fromEntries(
            Object.entries(rest).filter(
              ([_, value]) =>
                value != null &&
                typeof value !== 'object' &&
                typeof value !== 'function',
            ),
          ),
        };
      };

      return {
        ...state,
        routes: R.update(
          lastViewableRouteIdx,
          {
            ...lastViewableRoute,
            params: cleanParams(lastViewableRoute.params),
            state: newState,
          },
          state.routes as any,
        ) as any,
        index: lastViewableRouteIdx,
      };
    }
  },
  getStateFromPath: (path, options) => {
    // exclude routes that don't expect to loaded directly w/ prefixing it with home.
    if (
      (path.startsWith('/shopifyClips') ||
        path.startsWith('/products/') ||
        path.startsWith('/csClips')) &&
      Platform.OS === 'web' &&
      isMobileLayout()
    ) {
      options = Object.assign({}, options, {
        initialRouteName: undefined,
      });
    }

    const state = resolveStateFromPath(path, options);

    if (state == null) {
      return state;
    }

    if (!getInitialPath()) {
      // save the initial path for future use where necessary.
      setInitialPath(path);

      tryCaptureCampaignId(path);

      // save the initial navigation state so that we can make the initial
      // navigation later on "home" page.
      setInitialState(state);
    }

    if (
      Platform.OS === 'web' &&
      state?.routes != null &&
      state?.routes?.length > 1
    ) {
      // always redirect to the homepage first, then navigate to the target
      // page from the homepage so that when we "go back", we can go back to
      // home page no matter which the previous page is.
      return getStateFromPath('/', options);
    }

    // for mobile apps, the resolved state suffices all cases.
    return state;
  },
  config: {
    // this will make sure our app always starts with discovery
    initialRouteName: 'home',
    screens: screens,
  },
  subscribe: listener => {
    const sub = Linking.addEventListener('url', async ({ url }) => {
      debug('url changed: %s', url);
      tryCaptureCampaignId(url);

      const publicConfig = await firstValueFrom(publicConfigQuery$);
      const authRequired = publicConfig.auth?.required_for_mobile !== false;

      if (Platform.OS !== 'web' && authRequired && !currentUserId()) {
        // postpone url request util user logged in
        updateInitialUrl(url);
        return;
      }

      // otherwise let navigation handle the url
      listener(url);
    });

    return () => sub.remove();
  },
};
